import React, { useState } from "react";
import isEqual from "react-fast-compare";

import { css } from "@emotion/react";
import { TechSpecsSection } from "@swo/storefront";
import cn from "classnames";
import { NonEmptyArray } from "fp-ts/NonEmptyArray";
import { IconMinus, IconPlus } from "../../../assets";
import { ContentContainer } from "../../../components/_shared-components/arrangement/Container";
import { Spacer } from "../../../components/_shared-components/arrangement/Space";
import { CSS_MQ } from "../../../styles/breakpoints";
import { fontColor, FONTS, fontSize } from "../../../styles/fonts";
import { CSS_COLORS } from "../../../styles/styles-css-variables";
import { ReactTFC } from "../../../utils/fp/missing-ts-parts";
import { comparisonIconForGroup } from "../../product-technical-specification-page-layout/comparison-icon-for-group";
import { ComparisonSections } from "../../product-technical-specification-page-layout/sections/comparison-sections";
import { PDPHeadline } from "../pdp-headline";
import { T } from "../../../components/T";
import type { TranslatedValue } from "@swo/storefront";

const _ProductDetailPageTechnicalSpecification: ReactTFC<{
  technicalSpecificationHeadline: TranslatedValue;
  moreInfoLabel: TranslatedValue;
  sections: NonEmptyArray<TechSpecsSection>;
}> = props => {
  const [expandedSection, setExpandedSection] = useState(0);

  const wrapperStyles = css`
    display: flex;
    flex-direction: column;

    --w: 24px;
    --g: 24px;

    ${CSS_MQ.atLeastMd} {
      --w: 32px;
      --g: 32px;
    }

    gap: 0;
  `;

  return (
    <>
      <Spacer size={"lg"} />
      <ContentContainer>
        <PDPHeadline className={"margin-big"}>
          <T>{props.technicalSpecificationHeadline}</T>
        </PDPHeadline>

        <div css={wrapperStyles}>
          {props.sections.map((section, idx) => {
            const Icon = comparisonIconForGroup(section.__key);

            const css1 = css`
              width: 48px;
              height: 48px;
            `;

            const isSelected = idx === expandedSection;

            const className = cn({ selected: isSelected, first: idx === 0 });

            return (
              <div
                key={section.__key}
                className={className}
                css={css`
                  &.selected {
                    margin-bottom: 12px;
                    ${CSS_MQ.atLeastMd} {
                      margin-bottom: 30px;
                    }
                  }
                `}
              >
                <button
                  onClick={() => setExpandedSection(idx)}
                  css={css`
                    display: block;
                    width: 100%;
                  `}
                >
                  <div css={barStyles} className={className}>
                    <Icon css={iconStyle} />

                    <span
                      css={[
                        FONTS.SwarovskiFutura500,
                        fontColor.black,
                        fontSize.x2,
                        css`
                          text-transform: uppercase;
                          text-align: left;
                        `,
                      ]}
                    >
                      <T>{section.headline}</T>
                    </span>

                    <div>{isSelected ? <IconMinus css={css1} /> : <IconPlus css={css1} />}</div>
                  </div>
                </button>

                <div
                  className={cn({ "is-selected": isSelected })}
                  css={css`
                    ${CSS_MQ.atLeastMd} {
                      margin-top: 20px;
                    }

                    &:not(.is-selected) {
                      display: none;
                    }
                  `}
                >
                  <ComparisonSections {...section} />
                </div>
              </div>
            );
          })}
        </div>
      </ContentContainer>
    </>
  );
};

export const ProductDetailPageTechnicalSpecification = React.memo(_ProductDetailPageTechnicalSpecification, isEqual);

const barStyles = css`
  display: grid;
  align-items: center;
  gap: 30px;
  padding: 8px 0;

  grid-template-columns: auto 1fr 40px;

  border-width: 0;

  &.first {
    border-top-width: 1px;
  }

  &:not(.selected) {
    border-bottom-width: 1px;
  }

  border-style: solid;
  border-color: ${CSS_COLORS.Grey5};
`;

const iconStyle = css`
  display: block;
  color: black;

  width: var(--w);
  height: var(--w);
`;
